import { useCallback, useMemo } from "react";
import useToasts from "@ui/Toast/useToasts";
import type { DashboardBlockModelT, DashboardModelT } from "../../schema/dashboard";
import { useDelete, useGet, usePost, usePut } from "../../../../../api/useCall";
import { omit as omitObject, pick as pickObject } from "diagnostics-lib/object";
import type { BlockHeadingModelT } from "../../schema/block/heading";
import type { BlockTextModelT } from "../../schema/block/text";
import type { BlockPlotModelT } from "../../schema/block/plot";
// import useQuery, { useRevalidate } from "../../../../../api/useQuery";
import { useChannel } from "../../../../../api/ws/useChannel";
import type { BlockImageModelT } from "../../schema/block/image";
import {
  mapPayloadFromBlockDiagram,
  mapPayloadFromBlockHeading,
  mapPayloadFromBlockImage,
  mapPayloadToBlockHeading,
  mapPayloadToBlockImage,
  mapPayloadToBlockPlot,
  mapPayloadToBlockText,
  mapPayloadFromBlockPlot,
  mapPayloadFromBlockText,
  mapPayloadToBlockDiagram,
  mapPayloadFromBlockComment,
  mapPayloadToBlockComment,
  mapPayloadToBlockPreset,
  mapPayloadFromBlockPreset,
} from "./utils/mapping";
import type { BlockDiagramModelT } from "../../schema/block/diagram";
import type { Output } from "../../../../../api/call";
import type { BlockCommentModelT } from "../../schema/block/comment";
import type { BlockPresetModelT } from "../../schema/block/preset";
import { useQuery } from "@tanstack/react-query";
import { MINUTE_MS } from "@ui/i18n/formatDate";
import useDistinctShape from "@ui/lib/hooks/useDistinctShape";

const placeholderData = { dashboards: [], blocks: [] };

export const useDashboards = (projectId: string) => {
  const getDashboards = useGet("/projects/:projectId(uuid)/dashboards");
  const queryFn = useCallback(
    () => getDashboards({ routeParams: { projectId } }),
    [projectId, getDashboards],
  );
  const select = useCallback(
    ({
      body: dashboards,
    }: {
      body: Output<"GET", "/projects/:projectId(uuid)/dashboards">["body"];
    }) => {
      if (dashboards.length === 0) return placeholderData;

      const dashboardResults = dashboards.map(dashboard => ({
        ...pickObject(dashboard, [
          "id",
          "projectId",
          "title",
          "published",
          "description",
          "readonly",
          "createdByUserId",
          "createdByUserName",
          "updatedByUserName",
        ]),
        createdAt: new Date(dashboard.createdAt).getTime(),
        updatedAt: new Date(dashboard.updatedAt).getTime(),
        blocks: [
          ...(dashboard.headings ?? []),
          ...(dashboard.plots ?? []),
          ...(dashboard.texts ?? []),
          ...(dashboard.diagrams ?? []),
          ...(dashboard.images ?? []),
          ...(dashboard.comments ?? []),
          ...(dashboard.presets ?? []),
        ].map(block => block.id),
      }));

      const blockResults = dashboards.flatMap(
        ({ texts, headings, plots, images, diagrams, comments, presets }) => {
          const nextTexts = (texts ?? []).map(mapPayloadToBlockText);
          const nextComments = (comments ?? []).map(mapPayloadToBlockComment);
          const nextPresets = (presets ?? []).map(mapPayloadToBlockPreset);
          const nextHeadings = (headings ?? []).map(mapPayloadToBlockHeading);
          const nextImages = (images ?? []).map(mapPayloadToBlockImage);
          const nextPlots = (plots ?? []).map(mapPayloadToBlockPlot);
          const nextDiagrams = (diagrams ?? []).map(mapPayloadToBlockDiagram);
          return [
            ...nextTexts,
            ...nextHeadings,
            ...nextPlots,
            ...nextImages,
            ...nextDiagrams,
            ...nextComments,
            ...nextPresets,
          ];
        },
      );

      return { dashboards: dashboardResults, blocks: blockResults };
    },
    [],
  );

  const queryKey = useMemo(() => ["/projects/:projectId(uuid)/dashboards", projectId], [projectId]);

  const { data, refetch } = useQuery({
    keepPreviousData: false,
    suspense: true,
    staleTime: 200 * MINUTE_MS,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    queryKey,
    queryFn,
    select,
  });

  useChannel(
    {
      key: "dashboards",
      projectId,
    },
    () => refetch(),
    true,
    true,
  );

  return useDistinctShape(data ?? placeholderData);
};

export function usePublishedDashboards(projectId: string) {
  const { dashboards } = useDashboards(projectId);
  return useMemo(() => dashboards.filter(d => d.published), [dashboards]);
}

export function useDashboard(projectId: string, dashboardId: string) {
  const { dashboards, blocks } = useDashboards(projectId);
  return useMemo(
    () => ({
      dashboard: dashboards.find(({ id }) => id === dashboardId),
      blocks: blocks.filter(block => block.dashboardId === dashboardId),
    }),
    [dashboards, blocks, dashboardId],
  );
}

export type Dashboard = ReturnType<typeof useDashboards>["dashboards"][number];

export const useCreateRemoteDashboardCallback = () => {
  const { addToast } = useToasts();
  const createDashboard = usePost("/projects/:projectId(uuid)/dashboards");

  return useCallback(
    (
      dashboard: Pick<
        DashboardModelT,
        "projectId" | "title" | "description" | "readonly" | "published"
      >,
      blocks?: DashboardBlockModelT[],
    ) =>
      createDashboard({
        routeParams: { projectId: dashboard.projectId },
        body: {
          ...dashboard,
          readonly: dashboard.readonly ?? false,
          block: mapPayloadFromBlocks(blocks ?? []),
        },
      })
        .then(mapResponse)
        .catch(() => {
          addToast("error", "Failed to synchronize dashboard");
          return null;
        }),
    [addToast, createDashboard],
  );
};

export const useUpdateRemoteDashboardCallback = () => {
  const { addToast } = useToasts();
  const updateDashboard = usePut("/projects/:projectId(uuid)/dashboards/:dashboardId(uuid)");

  return useCallback(
    (dashboard: DashboardModelT, blocks: DashboardBlockModelT[]) =>
      updateDashboard({
        routeParams: { projectId: dashboard.projectId, dashboardId: dashboard.id },
        body: {
          ...pickObject(dashboard, ["title", "published", "description"]),
          readonly: dashboard.readonly ?? false,
          block: mapPayloadFromBlocks(blocks ?? []),
        },
      })
        .then(mapResponse)
        .catch(() => {
          addToast("error", "Failed to synchronize dashboard");
        }),
    [addToast, updateDashboard],
  );
};

export const useDeleteRemoteDashboardCallback = () => {
  const { addToast } = useToasts();
  const deleteDashboard = useDelete("/projects/:projectId(uuid)/dashboards/:dashboardId(uuid)");

  return useCallback(
    (projectId: string, dashboardId: string) =>
      deleteDashboard({
        routeParams: { projectId, dashboardId },
      }).catch(() => {
        addToast("error", "Failed to synchronize dashboard");
      }),
    [addToast, deleteDashboard],
  );
};

export const useDuplicateDashboardCallback = () => {
  const { addToast } = useToasts();
  const duplicateDashboard = usePost(
    "/projects/:projectId(uuid)/dashboards/:dashboardId(uuid)/duplicate",
  );

  return useCallback(
    (dashboard: DashboardModelT) =>
      duplicateDashboard({
        routeParams: { projectId: dashboard.projectId, dashboardId: dashboard.id },
        body: {
          title: dashboard.title,
          published: dashboard.published,
        },
      })
        .then(res => res.body)
        .catch(() => {
          addToast("error", "Failed to duplicate dashboard");
        }),
    [addToast, duplicateDashboard],
  );
};

function mapResponse(
  res:
    | Output<"POST", "/projects/:projectId(uuid)/dashboards">
    | Output<"PUT", "/projects/:projectId(uuid)/dashboards/:dashboardId(uuid)">,
): { dashboard: DashboardModelT; blocks: DashboardBlockModelT[] } {
  return {
    dashboard: mapPayloadToDashboard(res),
    blocks: [
      ...res.body.texts.map(mapPayloadToBlockText),
      ...res.body.headings.map(mapPayloadToBlockHeading),
      ...res.body.images.map(mapPayloadToBlockImage),
      ...res.body.plots.map(mapPayloadToBlockPlot),
      ...res.body.diagrams.map(mapPayloadToBlockDiagram),
      ...res.body.comments.map(mapPayloadToBlockComment),
      ...res.body.presets.map(mapPayloadToBlockPreset),
    ],
  };
}

function mapPayloadToDashboard(
  payload:
    | Output<"POST", "/projects/:projectId(uuid)/dashboards">
    | Output<"PUT", "/projects/:projectId(uuid)/dashboards/:dashboardId(uuid)">,
): DashboardModelT {
  return {
    ...omitObject(payload.body, [
      "plots",
      "texts",
      "headings",
      "comments",
      "presets",
      "createdAt",
      "updatedAt",
    ]),
    createdAt: new Date(payload.body.createdAt).getTime(),
    updatedAt: new Date(payload.body.updatedAt).getTime(),
    blocks: [
      ...payload.body.plots,
      ...payload.body.texts,
      ...payload.body.comments,
      ...payload.body.presets,
      ...payload.body.headings,
    ].map(block => block.id),
  };
}

function mapPayloadFromBlocks(blocks: DashboardBlockModelT[]) {
  const { texts, headings, images, plots, diagrams, comments, presets } = filterBlockByKind(
    blocks ?? [],
  );
  return {
    texts: texts.map(mapPayloadFromBlockText) ?? [],
    headings: headings.map(mapPayloadFromBlockHeading) ?? [],
    images: images.map(mapPayloadFromBlockImage) ?? [],
    plots: plots.map(mapPayloadFromBlockPlot) ?? [],
    diagrams: diagrams.map(mapPayloadFromBlockDiagram) ?? [],
    comments: comments.map(mapPayloadFromBlockComment) ?? [],
    presets: presets.map(mapPayloadFromBlockPreset) ?? [],
  };
}

function filterBlockByKind(blocks: DashboardBlockModelT[]): {
  texts: BlockTextModelT[];
  headings: BlockHeadingModelT[];
  images: BlockImageModelT[];
  plots: BlockPlotModelT[];
  diagrams: BlockDiagramModelT[];
  comments: BlockCommentModelT[];
  presets: BlockPresetModelT[];
} {
  return blocks?.reduce<{
    texts: BlockTextModelT[];
    headings: BlockHeadingModelT[];
    images: BlockImageModelT[];
    diagrams: BlockDiagramModelT[];
    plots: BlockPlotModelT[];
    comments: BlockCommentModelT[];
    presets: BlockPresetModelT[];
  }>(
    (acc, block) => {
      if (block.kind === "TEXT") acc.texts.push(block);
      if (block.kind === "HEADING") acc.headings.push(block);
      if (block.kind === "IMAGE") acc.images.push(block);
      if (block.kind === "PLOT") acc.plots.push(block);
      if (block.kind === "DIAGRAM") acc.diagrams.push(block);
      if (block.kind === "COMMENT") acc.comments.push(block);
      if (block.kind === "PRESET") acc.presets.push(block);
      return acc;
    },
    { texts: [], headings: [], images: [], plots: [], diagrams: [], comments: [], presets: [] },
  );
}
